(function () {
	const FIREBASE_VERSION = "10.13.2";
	const INSTALLATION_ID_KEY = "push_notifications.installation_id";
	const SESSION_REGISTRATION_KEY = "push_notifications.last_registration";
	const GLOBAL_INIT_KEY = "__pushNotificationsInitPromise";
	const BANNER_ID = "push-notifications-banner-anchor";
	const BANNER_THEME = "push-notifications-banner";

	if (window[GLOBAL_INIT_KEY]) {
		return;
	}

	setupDebugHelpers();
	window[GLOBAL_INIT_KEY] = bootstrap();

	async function bootstrap() {
		const config = getPublicConfig();
		if (!config || !config.enabled || String(config.provider || "").toUpperCase() !== "FCM") {
			return;
		}

		const user = getCurrentUser();
		if (!user || user === "Guest") {
			return;
		}

		if (!supportsPushNotifications()) {
			return;
		}

		const serviceWorkerRegistration = await registerServiceWorker(config);
		const standardWebPushFlow = shouldUseStandardWebPush();
		const permission = await resolvePermission(
			config.auto_register_on_login,
			standardWebPushFlow || shouldDelayPermissionRequest()
		);
		const installationId = getInstallationId();

		if (permission !== "granted") {
			showNotificationBanner({
				config,
				installationId,
				serviceWorkerRegistration,
				permission,
			});
			await unregisterInstallation(installationId);
			return;
		}

		hideNotificationBanner();
		await finishPushRegistration({
			config,
			installationId,
			serviceWorkerRegistration,
			permission,
		});
	}

	function getPublicConfig() {
		if (!window.frappe || !window.frappe.boot) {
			return null;
		}
		return window.frappe.boot.push_notifications || null;
	}

	function setupDebugHelpers() {
		window.pushNotificationsDebug = Object.assign({}, window.pushNotificationsDebug, {
			showTestBanner,
			hideBanner: hideNotificationBanner,
		});
	}

	function getCurrentUser() {
		if (window.frappe && window.frappe.session && window.frappe.session.user) {
			return window.frappe.session.user;
		}
		if (window.frappe && window.frappe.boot && window.frappe.boot.user) {
			if (typeof window.frappe.boot.user === "string") {
				return window.frappe.boot.user;
			}
			return window.frappe.boot.user.name || null;
		}
		return null;
	}

	function supportsPushNotifications() {
		return Boolean(
			window.Notification &&
				window.localStorage &&
				window.crypto &&
				window.crypto.subtle &&
				navigator.serviceWorker
		);
	}

	function shouldDelayPermissionRequest() {
		return isIos() || isMobileDevice();
	}

	async function resolvePermission(autoRegisterOnLogin, requiresUserGesture) {
		if (Notification.permission === "granted" || Notification.permission === "denied") {
			return Notification.permission;
		}

		if (!autoRegisterOnLogin || requiresUserGesture) {
			return Notification.permission;
		}

		return Notification.requestPermission();
	}

	function getInstallationId() {
		let installationId = window.localStorage.getItem(INSTALLATION_ID_KEY);
		if (!installationId) {
			installationId = generateInstallationId();
			window.localStorage.setItem(INSTALLATION_ID_KEY, installationId);
		}
		return installationId;
	}

	function generateInstallationId() {
		if (window.crypto && typeof window.crypto.randomUUID === "function") {
			return window.crypto.randomUUID();
		}

		const bytes = new Uint8Array(16);
		window.crypto.getRandomValues(bytes);
		return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
	}

	async function loadFirebaseCompat() {
		await loadScriptOnce(
			"https://www.gstatic.com/firebasejs/" + FIREBASE_VERSION + "/firebase-app-compat.js"
		);
		await loadScriptOnce(
			"https://www.gstatic.com/firebasejs/" + FIREBASE_VERSION + "/firebase-messaging-compat.js"
		);
	}

	function loadScriptOnce(src) {
		const existing = document.querySelector('script[data-push-notifications-src="' + src + '"]');
		if (existing) {
			return Promise.resolve();
		}

		return new Promise(function (resolve, reject) {
			const script = document.createElement("script");
			script.src = src;
			script.async = true;
			script.dataset.pushNotificationsSrc = src;
			script.onload = resolve;
			script.onerror = reject;
			document.head.appendChild(script);
		});
	}

	async function registerServiceWorker(config) {
		const serviceWorkerUrl = new URL("/firebase-messaging-sw.js", window.location.origin);
		serviceWorkerUrl.search = new URLSearchParams({
			apiKey: sanitizeConfigValue(config.web_api_key),
			authDomain: sanitizeConfigValue(config.web_auth_domain),
			projectId: sanitizeConfigValue(config.project_id),
			messagingSenderId: sanitizeConfigValue(config.messaging_sender_id),
			appId: sanitizeConfigValue(config.web_app_id),
			vapidPublicKey: sanitizeConfigValue(config.vapid_public_key),
			defaultClickAction: sanitizeConfigValue(config.default_click_action),
			defaultIconUrl: sanitizeConfigValue(config.default_icon_url),
			defaultBadgeUrl: sanitizeConfigValue(config.default_badge_url),
		}).toString();

		return navigator.serviceWorker.register(serviceWorkerUrl.toString(), { scope: "/" });
	}

	function getMessagingInstance(config) {
		const appName = "push_notifications";
		const existingApp = window.firebase.apps.find(function (app) {
			return app.name === appName;
		});
		const firebaseApp =
			existingApp ||
			window.firebase.initializeApp(
				{
					apiKey: config.web_api_key,
					authDomain: sanitizeConfigValue(config.web_auth_domain),
					projectId: sanitizeConfigValue(config.project_id),
					messagingSenderId: sanitizeConfigValue(config.messaging_sender_id),
					appId: sanitizeConfigValue(config.web_app_id),
				},
				appName
			);
		return window.firebase.messaging(firebaseApp);
	}

	async function maybeRegisterDevice(context) {
		const secretMaterial = context.subscription
			? JSON.stringify(context.subscription)
			: String(context.token || "");
		const tokenHash = await sha256(secretMaterial);
		const browserMetadata = detectBrowserMetadata();
		const signature = JSON.stringify({
			user: context.user,
			installation_id: context.installationId,
			token_hash: tokenHash,
			permission: context.permission,
		});
		if (window.sessionStorage.getItem(SESSION_REGISTRATION_KEY) === signature) {
			return;
		}

		await frappe.call({
			method: "push_notifications.api.register_device",
			args: {
				installation_id: context.installationId,
				platform: "web",
				device_label: browserMetadata.device_label,
				browser: browserMetadata.browser,
				os: browserMetadata.os,
				pwa_installed: browserMetadata.pwa_installed ? 1 : 0,
				notification_permission: context.permission,
				token: context.token,
				subscription: context.subscription || undefined,
			},
		});

		window.sessionStorage.setItem(SESSION_REGISTRATION_KEY, signature);
	}

	async function registerStandardWebPushDevice(
		config,
		installationId,
		serviceWorkerRegistration,
		permission
	) {
		const user = getCurrentUser();
		const browserMetadata = detectBrowserMetadata();
		const existingSubscription = await serviceWorkerRegistration.pushManager.getSubscription();
		const subscription =
			existingSubscription ||
			(await serviceWorkerRegistration.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: urlBase64ToUint8Array(sanitizeConfigValue(config.vapid_public_key)),
			}));

		await maybeRegisterDevice({
			user,
			subscription: subscription.toJSON(),
			permission,
			installationId,
		});

		if (browserMetadata.pwa_installed) {
			hideNotificationBanner();
		}
	}

	async function finishPushRegistration(context) {
		if (shouldUseStandardWebPush()) {
			await registerStandardWebPushDevice(
				context.config,
				context.installationId,
				context.serviceWorkerRegistration,
				context.permission
			);
			hideNotificationBanner();
			return;
		}

		await loadFirebaseCompat();
		const messaging = getMessagingInstance(context.config);
		const token = await messaging.getToken({
			vapidKey: sanitizeConfigValue(context.config.vapid_public_key),
			serviceWorkerRegistration: context.serviceWorkerRegistration,
		});

		if (!token) {
			return;
		}

		await maybeRegisterDevice({
			user: getCurrentUser(),
			token,
			permission: context.permission,
			installationId: context.installationId,
		});
		setupForegroundNotifications(messaging, context.serviceWorkerRegistration, context.config);
		hideNotificationBanner();
	}

	async function unregisterInstallation(installationId) {
		if (!window.frappe || !window.frappe.call || !installationId || getCurrentUser() === "Guest") {
			return;
		}

		try {
			await frappe.call({
				method: "push_notifications.api.unregister_device",
				args: { installation_id: installationId },
			});
		} catch (error) {
			// Keep failures silent on the client; unregister is best-effort.
		}
	}

	function setupForegroundNotifications(messaging, serviceWorkerRegistration, config) {
		messaging.onMessage(function (payload) {
			if (document.visibilityState === "visible" || Notification.permission !== "granted") {
				return;
			}

			const notification = payload.notification || payload.data || {};
			const title = notification.title || "Notification";
			const options = {
				body: notification.body || "",
				icon: notification.icon || config.default_icon_url || undefined,
				badge: notification.badge || config.default_badge_url || undefined,
				data: {
					click_action:
						(notification.click_action || payload.data && payload.data.click_action || config.default_click_action || "/app"),
				},
			};
			serviceWorkerRegistration.showNotification(title, options);
		});
	}

	function shouldUseStandardWebPush() {
		return isIosHomeScreenWebApp() && supportsStandardWebPush();
	}

	function supportsStandardWebPush() {
		return Boolean(window.PushManager && navigator.serviceWorker);
	}

	function isIosHomeScreenWebApp() {
		return isIos() && isPwaInstalled();
	}

	function isIos() {
		const userAgent = navigator.userAgent || "";
		return /iPhone|iPad|iPod/i.test(userAgent);
	}

	function isMobileDevice() {
		const userAgent = navigator.userAgent || "";
		return /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent);
	}

	function detectBrowserMetadata() {
		const userAgent = navigator.userAgent || "";
		return {
			browser: detectBrowser(userAgent),
			os: detectOperatingSystem(userAgent),
			pwa_installed: isPwaInstalled(),
			device_label: buildDeviceLabel(userAgent),
		};
	}

	function detectBrowser(userAgent) {
		if (/CriOS\//.test(userAgent)) {
			return "Chrome iOS";
		}
		if (/FxiOS\//.test(userAgent)) {
			return "Firefox iOS";
		}
		if (/Edg\//.test(userAgent)) {
			return "Edge";
		}
		if (/Chrome\//.test(userAgent) && !/Edg\//.test(userAgent)) {
			return "Chrome";
		}
		if (/Firefox\//.test(userAgent)) {
			return "Firefox";
		}
		if (/Safari\//.test(userAgent) && !/Chrome\//.test(userAgent)) {
			return "Safari";
		}
		return "Unknown";
	}

	function detectOperatingSystem(userAgent) {
		if (/Windows/i.test(userAgent)) {
			return "Windows";
		}
		if (/Mac OS X/i.test(userAgent)) {
			return "macOS";
		}
		if (/Android/i.test(userAgent)) {
			return "Android";
		}
		if (/iPhone|iPad|iPod/i.test(userAgent)) {
			return "iOS";
		}
		if (/Linux/i.test(userAgent)) {
			return "Linux";
		}
		return "Unknown";
	}

	function isPwaInstalled() {
		if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
			return true;
		}
		return Boolean(window.navigator.standalone);
	}

	function buildDeviceLabel(userAgent) {
		const browser = detectBrowser(userAgent);
		const os = detectOperatingSystem(userAgent);
		return browser + " on " + os;
	}

	async function sha256(value) {
		const encoded = new TextEncoder().encode(value);
		const hashBuffer = await window.crypto.subtle.digest("SHA-256", encoded);
		return Array.from(new Uint8Array(hashBuffer), function (byte) {
			return byte.toString(16).padStart(2, "0");
		}).join("");
	}

	function sanitizeConfigValue(value) {
		if (typeof value !== "string") {
			return value || "";
		}
		const trimmed = value.trim();
		if (trimmed.startsWith("https://")) {
			return trimmed.replace(/^https:\/\//, "").replace(/\/+$/, "");
		}
		if (trimmed.startsWith("http://")) {
			return trimmed.replace(/^http:\/\//, "").replace(/\/+$/, "");
		}
		return trimmed;
	}

	function urlBase64ToUint8Array(base64String) {
		const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
		const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
		const rawData = window.atob(base64);
		return Uint8Array.from(rawData, function (char) {
			return char.charCodeAt(0);
		});
	}

	async function showNotificationBanner(context) {
		ensureBannerStyles();

		const anchor = ensureBannerAnchor();
		if (!ensureTippyAvailable()) {
			return;
		}

		const content = buildBannerContent(context);
		const existingInstance = anchor._tippy;
		if (existingInstance) {
			existingInstance.setContent(content);
			existingInstance.show();
			return;
		}

		const instance = window.tippy(anchor, {
			content,
			allowHTML: true,
			appendTo: document.body,
			arrow: false,
			interactive: true,
			maxWidth: 320,
			placement: isMobileDevice() ? "top" : "left-start",
			theme: BANNER_THEME,
			trigger: "manual",
		});
		instance.show();
	}

	function hideNotificationBanner() {
		const anchor = document.getElementById(BANNER_ID);
		anchor?._tippy?.destroy();
		anchor?.remove();
	}

	function buildBannerContent(context) {
		const wrapper = document.createElement("div");
		wrapper.className = "push-notifications-banner-card";

		const title = document.createElement("div");
		title.className = "push-notifications-banner-title";
		title.textContent = context.title || "Уведомления выключены";

		const body = document.createElement("div");
		body.className = "push-notifications-banner-body";
		body.textContent = context.message || getBannerMessage(context.permission);

		wrapper.appendChild(title);
		wrapper.appendChild(body);

		if (context.allowEnableButton !== false && context.permission !== "denied" && canPromptFromBanner()) {
			const button = document.createElement("button");
			button.type = "button";
			button.className = "push-notifications-banner-button btn btn-default btn-sm";
			button.textContent = "Включить";
			button.addEventListener("click", async function () {
				try {
					const permission = await Notification.requestPermission();
					if (permission === "granted") {
						await finishPushRegistration({
							config: context.config,
							installationId: context.installationId,
							serviceWorkerRegistration: context.serviceWorkerRegistration,
							permission,
						});
					} else {
						showNotificationBanner({
							config: context.config,
							installationId: context.installationId,
							serviceWorkerRegistration: context.serviceWorkerRegistration,
							permission,
						});
					}
				} catch (error) {
					console.error("Push banner registration failed", error);
				}
			});
			wrapper.appendChild(button);
		}

		return wrapper;
	}

	async function showTestBanner(options) {
		await showNotificationBanner({
			config: getPublicConfig() || {},
			installationId: "debug-banner",
			serviceWorkerRegistration: null,
			permission: options && typeof options.permission === "string" ? options.permission : "default",
			title:
				options && typeof options.title === "string" && options.title.trim()
					? options.title.trim()
					: "Тестовый баннер",
			message:
				options && typeof options.message === "string" && options.message.trim()
					? options.message.trim()
					: "Если ты видишь этот баннер, значит tippy и клиентский bundle подключены правильно.",
			allowEnableButton: false,
		});
	}

	function getBannerMessage(permission) {
		if (permission === "denied") {
			return "Разрешение на уведомления заблокировано. Включите его в настройках браузера для этого сайта.";
		}
		if (isIos() && !isPwaInstalled()) {
			return "На iPhone откройте сайт через «На экран Домой», затем нажмите «Включить».";
		}
		if (isIos() && isPwaInstalled()) {
			return "Нажмите «Включить», чтобы разрешить уведомления в приложении с домашнего экрана.";
		}
		if (isMobileDevice()) {
			return "Нажмите «Включить», чтобы получать уведомления на этом устройстве.";
		}
		return "Нажмите «Включить», чтобы получать браузерные уведомления в Desk.";
	}

	function canPromptFromBanner() {
		if (isIos() && !isPwaInstalled()) {
			return false;
		}
		return Notification.permission !== "denied";
	}

	function ensureBannerAnchor() {
		let anchor = document.getElementById(BANNER_ID);
		if (anchor) {
			return anchor;
		}

		anchor = document.createElement("button");
		anchor.id = BANNER_ID;
		anchor.type = "button";
		anchor.className = "btn btn-default btn-sm push-notifications-banner-anchor";
		anchor.textContent = "Уведомления";
		anchor.setAttribute("aria-label", "Уведомления выключены");
		anchor.style.cssText = [
			"position:fixed",
			"right:16px",
			"bottom:16px",
			"z-index:9998",
			"padding:10px 14px",
			"border-radius:999px",
			"border:1px solid var(--border-color)",
			"background:var(--fg-color)",
			"color:var(--text-color)",
			"box-shadow:0 4px 12px rgba(0,0,0,.08)",
			"font:600 13px/1.1 -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
			"letter-spacing:.01em",
		].join(";");
		document.body.appendChild(anchor);
		return anchor;
	}

	function ensureBannerStyles() {
		if (document.getElementById("push-notifications-banner-style")) {
			return;
		}

		const style = document.createElement("style");
		style.id = "push-notifications-banner-style";
		style.textContent = `
			.tippy-box[data-theme~="${BANNER_THEME}"] {
				background: var(--fg-color);
				border: 1px solid var(--border-color);
				border-radius: 16px;
				box-shadow: 0 12px 28px rgba(0, 0, 0, 0.1);
				color: var(--text-color);
			}
			.tippy-box[data-theme~="${BANNER_THEME}"] .tippy-content {
				padding: 0;
			}
			.push-notifications-banner-card {
				display: grid;
				gap: 8px;
				padding: 14px;
				min-width: 240px;
			}
			.push-notifications-banner-title {
				font: 600 14px/1.2 -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
				color: var(--heading-color, var(--text-color));
			}
			.push-notifications-banner-body {
				font: 500 13px/1.45 -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
				color: var(--text-color);
				opacity: 0.78;
			}
			.push-notifications-banner-anchor {
				min-height: 32px;
			}
			.push-notifications-banner-button {
				justify-self: start;
				margin-top: 2px;
			}
		`;
		document.head.appendChild(style);
	}

	function ensureTippyAvailable() {
		if (typeof window.tippy === "function") {
			return true;
		}
		console.error("push_notifications: window.tippy is not available. Rebuild assets.");
		return false;
	}
})();
